import { SiteUrls } from '@tableau/shared-urls';

import { UrlParamKeys } from '../../app/UrlParamKeys';
import { FeatureFlags } from '../FeatureFlags';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { AddUsersToDataAlertsParams, AddUsersToDataAlertsRequest } from '../ServerApi/requests/AddUsersToDataAlertsRequest';
import { DeleteDataAlertsRequest } from '../ServerApi/requests/DeleteDataAlertsRequest';
import { GetDataAlertsRequest } from '../ServerApi/requests/GetDataAlertsRequest';
import {
  RemoveUsersFromDataAlertsParams,
  RemoveUsersFromDataAlertsRequest,
} from '../ServerApi/requests/RemoveUsersFromDataAlertsRequest';
import { ResumeDataAlertsRequest } from '../ServerApi/requests/ResumeDataAlertsRequest';
import { SetDataAlertsOwnerRequest } from '../ServerApi/requests/SetDataAlertsOwnerRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { Resource } from './Resource';


export class DataAlerts extends Resource<IDataAlertInfo> {

  public static $inject = ['ErrorNotifyingServer', 'FetcherFactory'];

  constructor(private ErrorNotifyingServer: ErrorNotifyingServer, FetcherFactory: FetcherFactory) {
    super(FetcherFactory.getServerResourceFetcher(
      GetDataAlertsRequest,
      ServerApiMapper.dataAlerts));
  }

  public resumeAlerts(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids, idsBlock => new ResumeDataAlertsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public del(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids, idsBlock => new DeleteDataAlertsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetDataAlertsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public removeUsers(params: RemoveUsersFromDataAlertsParams): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(params.dataAlertIds,
          idsBlock => new RemoveUsersFromDataAlertsRequest({ dataAlertIds: idsBlock, userIds: params.userIds })),
      ResultHelper.combineResults);
  }

  public addUsers(params: AddUsersToDataAlertsParams): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(params.dataAlertIds,
          idsBlock => new AddUsersToDataAlertsRequest({ dataAlertIds: idsBlock, userIds: params.userIds })),
      ResultHelper.combineResults);
  }

  public userIsSubscribedToAllAlerts(alerts: Set<IDataAlertInfo>): boolean {
    return alerts.all(a => a.isCurrentUserSubscribed && a.recipientsCount > 0);
  }

  public userIsSubscribedToAnyAlerts(alerts: Set<IDataAlertInfo>): boolean {
    return alerts.any(a => a.isCurrentUserSubscribed && a.recipientsCount > 0);
  }

  public alertOnVizPath(alert: IDataAlertInfo, siteUrls: SiteUrls): string {
      let editPath = siteUrls.viewUrl(alert.context.path) + '?' + UrlParamKeys.EditDataAlert + '=' + alert.id;
      if (alert.context.type !== 'customizedView') {
        editPath += '&' + UrlParamKeys.OriginalView +'=y';
      }
      if(FeatureFlags.suspendDataAlerts && (alert.suspendState !== 'NotSuspended')) {
        editPath += '&' + UrlParamKeys.ResumeDataAlert + '=true';
      }
      return editPath.slice(2); // remove leading hash to use in location.url
  }

}

Module.instance().service('DataAlerts', DataAlerts);
